import { PlanScope } from '@/lib/types';
import { CurriculumDraft, PlanDraft } from '@/pages/planner/draft';

// Ephemeral per-visit UI state for the Planner screen — NOT persisted, NOT a
// store. Lives in a `useReducer` inside planner.tsx. The route already keys
// Planner on workspaceId (see PlannerRoute in app.tsx), so switching
// workspaces remounts the whole tree and resets this state for free.

// --- Builder context (which "thing" the builder is editing) ----------------
export type BuilderCtx =
  | { kind: 'dayPlan'; date: string } // creating/editing a plan scheduled on `date`
  | {
      kind: 'curriculumPlan';
      curriculumId: number;
      slotId: number;
      curriculumName: string;
      sortOrder: number;
      planCount: number;
      // Where Discard/Save should return to: curBuild by default, or
      // curSlotView when the builder was opened from that read-only screen's
      // "Edit plan" action.
      returnTo: 'curBuild' | 'curSlotView';
    };

// --- Screen union ------------------------------------------------------------
export type PlannerScreen =
  // `date` is present when a viewer selected a specific unscheduled date
  // (renders "No plan is scheduled for this date."); absent for the initial
  // no-selection state (renders "Select a date to schedule a practice or
  // view a plan.").
  | { name: 'empty'; date?: string }
  // A date's working panel is split into two slots — the shared workspace
  // plan and the caller's private "My Plan". `scope` on the day-anchored
  // screens is which slot is being viewed/created; the creation flows
  // (choice → modPast/curBrowse/curPlans → builder draft) thread it through
  // so the plan saves into the slot the user picked.
  | { name: 'choice'; date: string; scope: PlanScope }
  | { name: 'modPast'; date: string; scope: PlanScope }
  | { name: 'curBrowse'; intent: 'manage' | 'addToDate'; date?: string; scope?: PlanScope }
  | { name: 'curPlans'; curriculumId: number; date: string; scope: PlanScope }
  // Two steps of one wizard. `draft` is absent on a fresh curCreate (the
  // screen seeds a blank one) and present when curStructure hands it back via
  // Back, so nothing typed on either step is lost stepping between them.
  | { name: 'curCreate'; draft?: CurriculumDraft }
  | { name: 'curStructure'; draft: CurriculumDraft }
  | { name: 'curBuild'; curriculumId: number }
  | { name: 'builder'; ctx: BuilderCtx; initialDraft: PlanDraft }
  | { name: 'view'; date: string; scope: PlanScope }
  | { name: 'curSlotView'; curriculumId: number; slotId: number };

// --- Actions -----------------------------------------------------------------
export type PlannerAction =
  // Calendar picked a date; caller resolves whether a plan already exists.
  // `isViewer` is carried on the action (rather than read from a hook inside
  // the reducer) because plannerReducer is a pure function — viewers picking
  // an empty date must land on `empty` ("No plan is scheduled for this
  // date."), never `choice`, since every choice-screen option is a write
  // action. This is defense-in-depth layer 1; layer 2 is
  // working-panel-card.tsx refusing to render any write screen for viewers.
  | { type: 'SELECT_DATE'; date: string; hasWorkspacePlan: boolean; hasPersonalPlan: boolean; isViewer: boolean }
  | { type: 'CHOOSE_CREATE_NEW'; draft: PlanDraft }
  | { type: 'CHOOSE_MOD_PAST'; date: string; scope: PlanScope }
  | { type: 'USE_PAST_PLAN'; draft: PlanDraft }
  | { type: 'CHOOSE_CURRICULUM'; date?: string; intent: 'manage' | 'addToDate'; scope?: PlanScope }
  | { type: 'OPEN_CURRICULUM'; curriculumId: number; date?: string; intent: 'manage' | 'addToDate'; scope?: PlanScope }
  | { type: 'OPEN_CURRICULUM_SLOT_VIEW'; curriculumId: number; slotId: number }
  | {
      type: 'BUILD_SLOT';
      curriculumId: number;
      slotId: number;
      curriculumName: string;
      sortOrder: number;
      planCount: number;
      draft: PlanDraft;
    }
  | { type: 'ADD_SLOT_TO_DATE'; date: string; draft: PlanDraft }
  | { type: 'CREATE_CURRICULUM' }
  | { type: 'CURRICULUM_INFO_NEXT'; draft: CurriculumDraft }
  | { type: 'CURRICULUM_STRUCTURE_BACK'; draft: CurriculumDraft }
  | { type: 'CURRICULUM_CREATED'; curriculumId: number }
  | { type: 'SAVED_DAY_PLAN'; date: string; scope: PlanScope }
  | { type: 'SAVED_CURRICULUM_PLAN'; curriculumId: number; returnTo: 'curBuild' | 'curSlotView'; slotId: number }
  | { type: 'DISCARD_BUILDER'; fallback: PlannerScreen }
  | { type: 'EDIT_DAY_PLAN'; date: string; draft: PlanDraft }
  | {
      type: 'EDIT_CURRICULUM_PLAN';
      curriculumId: number;
      slotId: number;
      curriculumName: string;
      sortOrder: number;
      planCount: number;
      returnTo: 'curBuild' | 'curSlotView';
      draft: PlanDraft;
    }
  | { type: 'GO_TO'; screen: PlannerScreen };

export const initialPlannerScreen: PlannerScreen = { name: 'empty' };

export function plannerReducer(state: PlannerScreen, action: PlannerAction): PlannerScreen {
  switch (action.type) {
    case 'SELECT_DATE':
      // Workspace plan first (it's the leading tab); the user can switch to
      // their own plan via the scope tab strip.
      if (action.hasWorkspacePlan) return { name: 'view', date: action.date, scope: 'workspace' };
      if (action.hasPersonalPlan) return { name: 'view', date: action.date, scope: 'personal' };
      // Viewers never see the choice screen (every option there is a write
      // action) — fall back to the empty-state copy for an unscheduled date.
      return action.isViewer
        ? { name: 'empty', date: action.date }
        : { name: 'choice', date: action.date, scope: 'workspace' };

    case 'CHOOSE_CREATE_NEW':
      return { name: 'builder', ctx: { kind: 'dayPlan', date: action.draft.plan_date }, initialDraft: action.draft };

    case 'CHOOSE_MOD_PAST':
      return { name: 'modPast', date: action.date, scope: action.scope };

    case 'USE_PAST_PLAN':
      return { name: 'builder', ctx: { kind: 'dayPlan', date: action.draft.plan_date }, initialDraft: action.draft };

    case 'CHOOSE_CURRICULUM':
      return { name: 'curBrowse', intent: action.intent, date: action.date, scope: action.scope };

    case 'OPEN_CURRICULUM':
      return action.intent === 'manage'
        ? { name: 'curBuild', curriculumId: action.curriculumId }
        : {
            name: 'curPlans',
            curriculumId: action.curriculumId,
            date: action.date ?? '',
            scope: action.scope ?? 'workspace',
          };

    case 'OPEN_CURRICULUM_SLOT_VIEW':
      return { name: 'curSlotView', curriculumId: action.curriculumId, slotId: action.slotId };

    case 'BUILD_SLOT':
      return {
        name: 'builder',
        ctx: {
          kind: 'curriculumPlan',
          curriculumId: action.curriculumId,
          slotId: action.slotId,
          curriculumName: action.curriculumName,
          sortOrder: action.sortOrder,
          planCount: action.planCount,
          returnTo: 'curBuild',
        },
        initialDraft: action.draft,
      };

    case 'ADD_SLOT_TO_DATE':
      return { name: 'builder', ctx: { kind: 'dayPlan', date: action.date }, initialDraft: action.draft };

    case 'CREATE_CURRICULUM':
      return { name: 'curCreate' };

    case 'CURRICULUM_INFO_NEXT':
      return { name: 'curStructure', draft: action.draft };

    case 'CURRICULUM_STRUCTURE_BACK':
      return { name: 'curCreate', draft: action.draft };

    case 'CURRICULUM_CREATED':
      return { name: 'curBuild', curriculumId: action.curriculumId };

    case 'SAVED_DAY_PLAN':
      return { name: 'view', date: action.date, scope: action.scope };

    case 'SAVED_CURRICULUM_PLAN':
      return action.returnTo === 'curSlotView'
        ? { name: 'curSlotView', curriculumId: action.curriculumId, slotId: action.slotId }
        : { name: 'curBuild', curriculumId: action.curriculumId };

    case 'DISCARD_BUILDER':
      return action.fallback;

    case 'EDIT_DAY_PLAN':
      return { name: 'builder', ctx: { kind: 'dayPlan', date: action.date }, initialDraft: action.draft };

    case 'EDIT_CURRICULUM_PLAN':
      return {
        name: 'builder',
        ctx: {
          kind: 'curriculumPlan',
          curriculumId: action.curriculumId,
          slotId: action.slotId,
          curriculumName: action.curriculumName,
          sortOrder: action.sortOrder,
          planCount: action.planCount,
          returnTo: action.returnTo,
        },
        initialDraft: action.draft,
      };

    case 'GO_TO':
      return action.screen;

    default:
      return state;
  }
}

// Returns the calendar-relevant date for a screen, if any. Used by
// working-panel-card.tsx's viewer write-screen guard to recover a date (for
// the "No plan is scheduled for this date." empty-variant copy) when a
// viewer's screen is refused and replaced with EmptyScreen.
export function dateOf(screen: PlannerScreen): string | undefined {
  switch (screen.name) {
    case 'choice':
    case 'modPast':
    case 'view':
      return screen.date;
    case 'curBrowse':
      return screen.date;
    case 'curPlans':
      return screen.date;
    case 'builder':
      return screen.ctx.kind === 'dayPlan' ? screen.ctx.date : undefined;
    default:
      return undefined;
  }
}
